import { InMemoryCache } from '@apollo/client';
import {
  createFragmentRegistry,
  NormalizedCacheObject
} from '@apollo/client/cache';
import { ApolloLink } from '@apollo/client/core';
import { ApolloClient } from '@apollo/client/core/ApolloClient';
import { setContext } from '@apollo/client/link/context';
import { createHttpLink } from '@apollo/client/link/http/createHttpLink';
import { RetryLink } from '@apollo/client/link/retry';

import { userCookies } from '@/shared/constants/cookies';
import * as gatewayFragments from '@/shared/graphql/gateway/fragments';
import { queryCountryConfigs } from '@/shared/graphql/gateway/queries';
import { initStorefrontApolloClient } from '@/shared/utils/storefront-client';

import { useUserStore } from '../pinia/modules/user';
import { getToken } from './get-token';
import { hobbiiSessionStorage } from './web-storage';

const recoveryLink = (
  onSuccess: (token: string) => void,
  onError: () => void
) =>
  new RetryLink({
    delay: {
      initial: 0
    },
    attempts: {
      max: 2,
      retryIf: async (error) => {
        if (error.statusCode === 401) {
          const token = await getToken(true);
          if (token) {
            onSuccess(token);
          } else {
            onError();
          }
          return !!token;
        }
        return false;
      }
    }
  });

const enrichLink = () =>
  setContext(async (_, { headers }) => {
    const sessionToken = hobbiiSessionStorage.getItem(userCookies.C_ID);

    const authTokenHeader = sessionToken
      ? { Authorization: `Bearer ${sessionToken}` }
      : {};

    return {
      headers: {
        ...headers,
        ...authTokenHeader,
        'Content-Type': 'application/json'
      }
    };
  });

const httpLink = (graphqlHost: string) =>
  createHttpLink({
    uri: graphqlHost
  });

const cache = new InMemoryCache({
  typePolicies: {
    CommunityQueries: {
      keyFields: ['__typename']
    },
    CrafterQueries: {
      keyFields: ['__typename']
    }
  },
  fragments: createFragmentRegistry(...Object.values(gatewayFragments))
});

export type ApolloClientInitOptions = {
  graphqlHost: string;
};

export const apolloClientInit = ({ graphqlHost }: ApolloClientInitOptions) => {
  const userStore = useUserStore();

  const setReceivedCustomer = (token?: string) => {
    userStore.setCustomer(token);
  };

  return new ApolloClient({
    link: ApolloLink.from([
      recoveryLink(setReceivedCustomer, userStore.cleanCustomer),
      enrichLink(),
      httpLink(graphqlHost)
    ]),
    cache
  });
};

const getStorefrontApolloClient = async (
  gatewayApolloClient: ApolloClient<NormalizedCacheObject>
) => {
  const shopifyDomain = `https://${window.Shopify.shop}`;
  const storeName = window.Shopify.shop.split('.')[0]; // TODO: any smarter way of getting the storeName?

  const countryConfigsResponse = await gatewayApolloClient.query(
    queryCountryConfigs()
  );

  const publicAccessToken =
    countryConfigsResponse.data?.countryConfigs?.find(
      (config) => storeName === config?.shopifyConfig?.storefront?.name
    )?.shopifyConfig?.storefront?.publicAccessToken ?? '';

  // TODO: What happens if we don't have a publicAccessToken? Should we throw? For now notify Bugsnag.
  if (!publicAccessToken) {
    // Features rely on this
    throw new Error(
      `No publicAccessToken received from Gateway for storeName: "${storeName}".`
    );
  }

  return initStorefrontApolloClient(shopifyDomain, publicAccessToken);
};

const getGatewayApolloClient = () =>
  apolloClientInit({
    graphqlHost: import.meta.env.VITE_HOBBII_GATEWAY
  });

export const getApolloClients = async () => {
  const gatewayApolloClient = getGatewayApolloClient();

  const storefrontApolloClient =
    await getStorefrontApolloClient(gatewayApolloClient);

  return {
    gateway: gatewayApolloClient,
    storefront: storefrontApolloClient
  };
};
